import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';

interface INotificationModal {
	openModal: boolean;
	setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	title: string;
	closeFunction?: () => void;
	onExternalCloseModal?: boolean;
	setOnExternalCloseModal?: React.Dispatch<React.SetStateAction<boolean>>;
	isHideClose?: boolean;
	hideBannerClose?: boolean;
	icon?: string;
	iconPosition?: 'right' | 'left';
	wide?: boolean;
	children?: any;
}

const NotificationModal: React.FC<INotificationModal> = ({
	openModal,
	setOpenModal,
	title,
	closeFunction,
	onExternalCloseModal,
	setOnExternalCloseModal,
	hideBannerClose,
	icon,
	iconPosition,
	wide,
	children,
}) => {
	const [prevOpenModal, setPrevOpenModal] = useState(false);
	const modalRef = useRef<HTMLSpanElement>(null);
	const closeRef = useRef<HTMLButtonElement>(null);
	const modalContentRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (openModal !== prevOpenModal) {
			if (openModal) {
				if (modalRef.current != null) {
					modalRef?.current?.click();
				}
			}
			setPrevOpenModal(openModal);
		}
	}, [openModal, prevOpenModal]);

	useLayoutEffect(() => {
		if (onExternalCloseModal) {
			if (closeRef.current != null) {
				if (typeof setOnExternalCloseModal === 'function') {
					setOnExternalCloseModal(false);
				}
				closeRef.current.click();
			}
		}
	}, [onExternalCloseModal, setOnExternalCloseModal]);

	const handleOnClose = useCallback(() => {
		if (typeof setOpenModal === 'function') {
			setOpenModal(false);
		}
		setPrevOpenModal(false);
		if (typeof closeFunction === 'function') {
			closeFunction();
		}
	}, [setOpenModal, closeFunction]);

	// Focus trap for accessibility
	useEffect(() => {
		if (!openModal) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				handleOnClose();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [openModal, handleOnClose]);

	// Focus close button when modal opens
	useEffect(() => {
		if (openModal && closeRef.current) {
			closeRef.current.focus();
		}
	}, [openModal]);

	const modalId = `modal-${title.toLowerCase().replace(/\s+/g, '-')}`;

	return (
		<Modal
			tabIndex={-1}
			show={openModal}
			onHide={handleOnClose}
			aria-labelledby={`${modalId}-title`}
			aria-modal='true'
			role='dialog'
		>
			<Modal.Dialog
				className='mx-auto border-0'
				role='document'
				ref={modalContentRef}
				style={{
					width: '90%',
					maxWidth: wide ? '1200px' : '',
					background: 'none',
				}}
			>
				<Modal.Header
					className='d-flex justify-content-center'
					style={{
						backgroundColor: 'var(--color-bg-elevated)',
						color: 'var(--color-text-primary)',
						borderBottom: '1px solid var(--color-border-subtle)',
					}}
				>
					<span className='position-relative w-100 text-center'>
						<h5
							id={`${modalId}-title`}
							className='modal-title text-center'
							style={{
								fontSize: '14px',
								fontFamily: 'var(--Inter-medium)',
								color: 'var(--color-text-primary)',
							}}
						>
							{iconPosition !== 'right' && icon != null && icon.length > 0 && (
								<span aria-hidden='true'>
									<i className={icon} />
									&nbsp;
								</span>
							)}
							{title}
							{iconPosition === 'right' && icon != null && icon.length > 0 && (
								<span aria-hidden='true'>
									&nbsp;
									<i className={icon} />
								</span>
							)}
						</h5>

						{!hideBannerClose && (
							<button
								type='button'
								className='position-absolute d-flex align-items-center justify-content-center'
								style={{
									top: 0,
									right: 0,
									height: '100%',
									cursor: 'pointer',
									background: 'transparent',
									border: 'none',
									padding: '8px',
									borderRadius: 'var(--radius-md)',
									color: 'var(--color-text-secondary)',
									transition: 'background-color var(--transition-fast), color var(--transition-fast)',
								}}
								id='closeBtnId'
								onClick={handleOnClose}
								ref={closeRef}
								aria-label='Close modal'
							>
								<i
									className='fas fa-times'
									aria-hidden='true'
								/>
							</button>
						)}
					</span>
				</Modal.Header>

				<Modal.Body
					style={{
						overflow: 'auto',
						backgroundColor: 'var(--color-bg-elevated)',
						color: 'var(--color-text-primary)',
					}}
				>
					{children}
				</Modal.Body>
			</Modal.Dialog>
		</Modal>
	);
};

export default NotificationModal;
